import React, { useEffect, useRef } from "react";
import { useAppDispatch } from "../../redux/hooks";

// WEBSOCKET
import { wsEndpoint } from "../../api/endpoints";
import { useParams, useLocation } from "react-router-dom";
import {
  updateFloorData,
  updateDeskStatus,
  updateDeskData,
  updateDeskInfoOnUnassignSensor,
  updateDeskInfoOnAssignSensor,
  updateNumberOfUnavailableDesks,
  updateSyncFlag,
} from "../../redux/slices/groups.slice";

import { useAuthenticator } from "@aws-amplify/ui-react";
import {
  setFileLink,
  updateFloorStats,
} from "../../redux/slices/reports.slice";
import {
  getDoors,
  setDoorWebsocketOpen,
  updateDoorData,
} from "../../redux/slices/doors.slice";
import { isDoorDataPayload } from "../../interfaces/doors";
import useManagedSignalingConnection from "../../hooks/useManagedSignalingConnection";

const doorRevalidationIntervalMs = 60 * 1000;

export function StateHandler() {
  const dispatch = useAppDispatch();
  const { id, GrpId } = useParams();

  const onSignalingHandlerRef = useRef<(msg: any) => void>(() => null);
  const pathname = useLocation()?.pathname || "";
  const isReportsPage = pathname.includes("reports");
  const normalisedPathname = pathname.replace(/\/$/, "");
  const isFloorPage = Boolean(
    id &&
      (normalisedPathname === `/${id}` ||
        normalisedPathname === `/${id}/presentation` ||
        (GrpId && normalisedPathname === `/Location/${GrpId}/${id}`)),
  );

  const { user } = useAuthenticator();
  console.log("USER: ", user);
  const userId = user?.getSignInUserSession()?.getIdToken()?.payload?.sub as
    | string
    | undefined;
  console.log("UserID: ", userId);
  const { isOpen, connectionCount, sendToServer } =
    useManagedSignalingConnection({
      socketURL: wsEndpoint,
      onMessage: (message) => onSignalingHandlerRef.current(message),
    });

  useEffect(() => {
    if (!isFloorPage || !id) {
      return;
    }

    dispatch(setDoorWebsocketOpen({ floorID: id, isOpen }));
    return () => {
      dispatch(setDoorWebsocketOpen({ floorID: id, isOpen: false }));
    };
  }, [dispatch, id, isFloorPage, isOpen]);

  useEffect(() => {
    if (!isFloorPage || !id) {
      return;
    }

    if (isOpen) {
      sendToServer({
        action: "subscribe",
        floorID: id,
        type: "location",
      });
    }

    // Keep REST reconciliation independent of the websocket. The protocol has
    // no subscription acknowledgement or replay, and REST is also the fallback
    // while reconnecting or during an extended websocket outage.
    const revalidate = () => dispatch(getDoors({ floorID: id }));
    revalidate();
    const interval = setInterval(revalidate, doorRevalidationIntervalMs);
    return () => clearInterval(interval);
  }, [
    id,
    GrpId,
    isFloorPage,
    isOpen,
    connectionCount,
    dispatch,
    sendToServer,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (isReportsPage && userId) {
      sendToServer({
        action: "subscribe",
        type: "reports",
        userID: userId,
      });
    }
  }, [
    isReportsPage,
    isOpen,
    connectionCount,
    userId,
    sendToServer,
  ]);

  onSignalingHandlerRef.current = (msg: any) => {
    switch (msg.action) {
      case "dataSummary": {
        dispatch(updateFloorData(msg.payload));

        break;
      }
      case "deskData": {
        dispatch(updateDeskData(msg.payload));
        break;
      }
      case "doorData": {
        if (
          isFloorPage &&
          isDoorDataPayload(msg.payload) &&
          msg.payload.floorID === id
        ) {
          dispatch(updateDoorData(msg.payload));
        }
        break;
      }
      case "sync-success": {
        dispatch(updateSyncFlag({ flag: "SUCCEEDED" }));
        break;
      }
      case "sync-failed": {
        dispatch(updateSyncFlag({ flag: "FAILED" }));
        break;
      }
      case "desk-status-update": {
        dispatch(updateDeskStatus(msg.payload));
        dispatch(updateNumberOfUnavailableDesks(msg.payload));
        break;
      }
      case "desk-assign-sensor": {
        dispatch(updateDeskInfoOnAssignSensor(msg.payload));

        break;
      }
      case "desk-unassign-sensor": {
        dispatch(updateDeskInfoOnUnassignSensor(msg.payload));
        break;
      }
      // assign/status events carry no counter snapshot, so they can't go
      // through the doorData merge — re-pull the floor's records instead
      // (the slice's revision guards make a refetch during live events safe).
      case "door-status-update":
      case "door-assign-sensor":
      case "door-unassign-sensor": {
        if (isFloorPage && id && msg.payload?.floorID === id) {
          dispatch(getDoors({ floorID: id }));
        }
        break;
      }
      case "wsSubscribe":
        if (
          !sendToServer({
            action: "subscribe",
            locationID: msg.locationID,
          })
        ) {
          console.warn(
            "Attempting to send message via websocket which hasnt been instantiated",
          );
        }
        break;
      case "wsHeartbeat":
        if (!sendToServer({ action: "heartbeat" })) {
          console.error(
            "Attempting to send heartbeat message via websocket which hasnt been instantiated",
          );
        }
        break;
      case "file-link": {
        dispatch(setFileLink(msg.payload));
        break;
      }
      case "statisticsSummary": {
        dispatch(updateFloorStats(msg.payload));
        break;
      }
    }
  };

  return <></>;
}

export default StateHandler;
